export const VANTAGE_TEMPLATE_FIRM_TYPES = [
    'Asset Manager / RIA',
    'Family Office',
    'Hedge Fund',
    'Endowment',
    'Corporate Development & M&A',
    'Private Equity / Venture Capital',
] as const;

export type VantageTemplateFirmType = (typeof VANTAGE_TEMPLATE_FIRM_TYPES)[number];

export type VantageQueryTemplate = {
    description: string;
    firmType: VantageTemplateFirmType;
    id: string;
    schemas: string[];
    sql: string;
    title: string;
};

const boundedLimit = (limit: number) => Math.min(Math.max(limit, 1), 1_000);

export const buildVantageQueryTemplates = (limit: number): VantageQueryTemplate[] => {
    const rowLimit = boundedLimit(limit);

    return [
        {
            id: 'ria-active-investments',
            firmType: 'Asset Manager / RIA',
            title: 'Active Investments by Market Type',
            description:
                'Summarize the active research inventory by public, private, and other market classifications.',
            schemas: ['investment'],
            sql: `SELECT market_type, COUNT(*) AS investment_count\nFROM investment\nWHERE archived IS NOT TRUE\nGROUP BY market_type\nORDER BY investment_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'ria-research-freshness',
            firmType: 'Asset Manager / RIA',
            title: 'Investment Research Freshness',
            description: 'Find investment records that have gone the longest without a research update.',
            schemas: ['investment'],
            sql: `SELECT name, market_type, financing_type, updated_at\nFROM investment\nWHERE archived IS NOT TRUE\nORDER BY updated_at ASC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'ria-memo-engagement',
            firmType: 'Asset Manager / RIA',
            title: 'Most Viewed Research Memos',
            description: 'Rank active memos by readership to understand which research is receiving attention.',
            schemas: ['memo'],
            sql: `SELECT title, document_type, views, updated_at\nFROM memo\nWHERE archived IS NOT TRUE\nORDER BY views DESC, updated_at DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'ria-open-task-workload',
            firmType: 'Asset Manager / RIA',
            title: 'Open Research Tasks by Priority',
            description: 'Review outstanding research work grouped by priority and due date.',
            schemas: ['task'],
            sql: `SELECT priority, COUNT(*) AS open_task_count, MIN(due_at) AS nearest_due_at\nFROM task\nWHERE completed IS NOT TRUE\nGROUP BY priority\nORDER BY open_task_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'family-office-private-investments',
            firmType: 'Family Office',
            title: 'Private Investments by Financing Type',
            description: 'Summarize private investment records across equity, debt, and other financing structures.',
            schemas: ['investment'],
            sql: `SELECT financing_type, COUNT(*) AS investment_count\nFROM investment\nWHERE market_type = 'Private' AND archived IS NOT TRUE\nGROUP BY financing_type\nORDER BY investment_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'family-office-opportunity-pipeline',
            firmType: 'Family Office',
            title: 'Opportunity Pipeline Inventory',
            description: 'Compare active opportunity pipelines by configured view and pipeline type.',
            schemas: ['pipeline'],
            sql: `SELECT view, type, COUNT(*) AS pipeline_count\nFROM pipeline\nWHERE archived IS NOT TRUE\nGROUP BY view, type\nORDER BY pipeline_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'family-office-governed-memos',
            firmType: 'Family Office',
            title: 'Research Memo Governance',
            description: 'Audit memo visibility, locking, and publication settings across the workspace.',
            schemas: ['memo'],
            sql: `SELECT type, locked, publicly_visible, COUNT(*) AS memo_count\nFROM memo\nWHERE archived IS NOT TRUE\nGROUP BY type, locked, publicly_visible\nORDER BY memo_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'family-office-folder-inventory',
            firmType: 'Family Office',
            title: 'Research Folder Inventory',
            description: 'Review how active workspace folders are organized and permissioned.',
            schemas: ['folder'],
            sql: `SELECT type, visibility, COUNT(*) AS folder_count\nFROM folder\nWHERE archived IS NOT TRUE\nGROUP BY type, visibility\nORDER BY folder_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'hedge-fund-investment-watchlist',
            firmType: 'Hedge Fund',
            title: 'Investment Update Watchlist',
            description: 'Surface active investment records in order of their most recent research update.',
            schemas: ['investment'],
            sql: `SELECT name, market_type, visibility, updated_at\nFROM investment\nWHERE archived IS NOT TRUE\nORDER BY updated_at DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'hedge-fund-research-readership',
            firmType: 'Hedge Fund',
            title: 'Research Readership Leaderboard',
            description: 'Rank memo readership alongside document and publication settings.',
            schemas: ['memo'],
            sql: `SELECT title, document_type, views, publicly_visible\nFROM memo\nWHERE archived IS NOT TRUE\nORDER BY views DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'hedge-fund-near-term-tasks',
            firmType: 'Hedge Fund',
            title: 'Near-Term Research Deadlines',
            description: 'Identify incomplete research tasks ordered by urgency and upcoming due date.',
            schemas: ['task'],
            sql: `SELECT title, priority, due_at, visibility\nFROM task\nWHERE completed IS NOT TRUE\nORDER BY due_at ASC, priority ASC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'hedge-fund-report-mix',
            firmType: 'Hedge Fund',
            title: 'Analytical Report Mix',
            description: 'Measure active analytical reports by type and workspace visibility.',
            schemas: ['report'],
            sql: `SELECT type, visibility, COUNT(*) AS report_count\nFROM report\nWHERE archived IS NOT TRUE\nGROUP BY type, visibility\nORDER BY report_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'endowment-investment-exposure',
            firmType: 'Endowment',
            title: 'Investment Inventory by Type',
            description: 'Create a governed inventory proxy across investment, market, and financing classifications.',
            schemas: ['investment'],
            sql: `SELECT type, market_type, financing_type, COUNT(*) AS investment_count\nFROM investment\nWHERE archived IS NOT TRUE\nGROUP BY type, market_type, financing_type\nORDER BY investment_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'endowment-dashboard-governance',
            firmType: 'Endowment',
            title: 'Dashboard Governance Overview',
            description: 'Audit dashboard visibility, public access, and lock status.',
            schemas: ['dashboard'],
            sql: `SELECT visibility, publicly_visible, locked, COUNT(*) AS dashboard_count\nFROM dashboard\nWHERE archived IS NOT TRUE\nGROUP BY visibility, publicly_visible, locked\nORDER BY dashboard_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'endowment-research-cadence',
            firmType: 'Endowment',
            title: 'Monthly Research Cadence',
            description: 'Track the number of research memos created each month.',
            schemas: ['memo'],
            sql: `SELECT DATE_TRUNC('month', created_at) AS month, COUNT(*) AS memo_count\nFROM memo\nGROUP BY DATE_TRUNC('month', created_at)\nORDER BY month DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'endowment-task-completion',
            firmType: 'Endowment',
            title: 'Diligence Task Completion',
            description: 'Compare completed and open diligence tasks by priority.',
            schemas: ['task'],
            sql: `SELECT completed, priority, COUNT(*) AS task_count\nFROM task\nGROUP BY completed, priority\nORDER BY completed, task_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'corp-dev-deal-pipelines',
            firmType: 'Corporate Development & M&A',
            title: 'Deal Pipeline Summary',
            description: 'Summarize active deal pipelines by view, type, and visibility.',
            schemas: ['pipeline'],
            sql: `SELECT view, type, visibility, COUNT(*) AS pipeline_count\nFROM pipeline\nWHERE archived IS NOT TRUE\nGROUP BY view, type, visibility\nORDER BY pipeline_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'corp-dev-diligence-queue',
            firmType: 'Corporate Development & M&A',
            title: 'Due Diligence Queue',
            description: 'Review incomplete deal tasks by due date and priority.',
            schemas: ['task'],
            sql: `SELECT title, priority, due_at, created_at\nFROM task\nWHERE completed IS NOT TRUE\nORDER BY due_at ASC, created_at ASC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'corp-dev-target-inventory',
            firmType: 'Corporate Development & M&A',
            title: 'Target Company Inventory',
            description: 'Review active investment targets alongside financing and market classifications.',
            schemas: ['investment'],
            sql: `SELECT name, market_type, financing_type, visibility, updated_at\nFROM investment\nWHERE archived IS NOT TRUE\nORDER BY updated_at DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'corp-dev-memo-readiness',
            firmType: 'Corporate Development & M&A',
            title: 'Deal Memo Readiness',
            description: 'Audit table-of-contents and source visibility across active deal memos.',
            schemas: ['memo'],
            sql: `SELECT document_type, table_of_contents_visible, sources_visible, COUNT(*) AS memo_count\nFROM memo\nWHERE archived IS NOT TRUE\nGROUP BY document_type, table_of_contents_visible, sources_visible\nORDER BY memo_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'pevc-financing-mix',
            firmType: 'Private Equity / Venture Capital',
            title: 'Portfolio Financing Mix',
            description: 'Summarize active investments by financing and market type.',
            schemas: ['investment'],
            sql: `SELECT financing_type, market_type, COUNT(*) AS investment_count\nFROM investment\nWHERE archived IS NOT TRUE\nGROUP BY financing_type, market_type\nORDER BY investment_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'pevc-pipeline-activity',
            firmType: 'Private Equity / Venture Capital',
            title: 'Pipeline Activity by View',
            description: 'Compare active sourcing and diligence pipelines by configured view.',
            schemas: ['pipeline'],
            sql: `SELECT view, COUNT(*) AS pipeline_count, MAX(updated_at) AS last_updated_at\nFROM pipeline\nWHERE archived IS NOT TRUE\nGROUP BY view\nORDER BY pipeline_count DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'pevc-ic-memo-readiness',
            firmType: 'Private Equity / Venture Capital',
            title: 'Investment Committee Memo Readiness',
            description: 'Review memo governance fields used to prepare investment committee materials.',
            schemas: ['memo'],
            sql: `SELECT title, document_type, sources_visible, table_of_contents_visible, updated_at\nFROM memo\nWHERE archived IS NOT TRUE\nORDER BY updated_at DESC\nLIMIT ${rowLimit};`,
        },
        {
            id: 'pevc-portfolio-workload',
            firmType: 'Private Equity / Venture Capital',
            title: 'Portfolio Workload by Priority',
            description: 'Summarize open portfolio and diligence work by priority and visibility.',
            schemas: ['task'],
            sql: `SELECT priority, visibility, COUNT(*) AS task_count\nFROM task\nWHERE completed IS NOT TRUE\nGROUP BY priority, visibility\nORDER BY task_count DESC\nLIMIT ${rowLimit};`,
        },
    ];
};
